import { WindowAggregation } from "@/gen_src/proto/chalk/graph/v1/graph_pb";
import { simpleRenderArrowType } from "@/utils/arrow-type";
import { compileOnlyAssertNever } from "@/utils/asserts";
import {
  ArrowTypeWrapper,
  FeatureInterface,
  FeatureInterfaceHasManyKind,
  FeatureInterfaceHasOneKind,
  FeatureInterfaceKind,
  FeatureInterfaceScalarKind,
  FeatureInterfaceWindowedFeatureKind,
  FeaturesOptions,
  GraphInterface,
  LegacyFeatureTypeInfo,
  VersionInfoInterface,
} from "@/utils/graph/GraphInterface";
import { fastCompact } from "@/utils/object-utils";
import { fqnToName } from "@/utils/resolver-utils";
import { Routes } from "@/utils/routes";
import { snakeToTitleCase } from "@/utils/text-case";
import {
  isWindowedChildName,
  transformWindowedName,
  windowPeriodAtEndRegex,
} from "@/utils/windowed.utils";
import { memoize } from "lodash";

export function featureIsWindowedParent(feature: FeatureInterface): boolean {
  return feature.kind.type === "windowed";
}

export function featureHasArrowType(feature: FeatureInterface): feature is FeatureInterface & {
  kind: FeatureInterfaceScalarKind & { typeInfo: ArrowTypeWrapper };
} {
  return (
    feature.kind.type === "scalar" &&
    feature.kind.typeInfo != null &&
    !("scalarKind" in feature.kind.typeInfo)
  );
}

export function prettyRenderFeatureType(
  typeInfo: LegacyFeatureTypeInfo | ArrowTypeWrapper,
): string {
  if ("scalarKind" in typeInfo) {
    return transformFeatureType(typeInfo.scalarKind);
  } else {
    return simpleRenderArrowType(typeInfo);
  }
}

export const transformFeatureType = (scalarKind: string): string => {
  const simplifiedScalarKind = scalarKind
    // kill `Windowed[actual_type]
    .replace(/Windowed\[(.*)]$/, "$1")
    // kill Optional[actual_type]
    .replace(/Optional\[(.*)]$/, "$1")
    // kill typing.actual_type
    .replace(/typing\.(.*)$/, "$1")
    // kill <class 'actual_type'>
    .replace(/<class '(.*)'>$/, "$1");
  return simplifiedScalarKind.replace(/\[([a-zA-Z0-9_]+.?)+]/, "[$1]");
};

export const nameForFeatureType = (
  feature: FeatureInterface,
  showDataframePrefix: boolean = true,
): string => {
  switch (feature.kind.type) {
    case "scalar":
    case "windowed":
    case "groupBy":
      return feature.kind.typeInfo != null
        ? prettyRenderFeatureType(feature.kind.typeInfo)
        : "<unknown>";
    case "hasMany":
      return showDataframePrefix
        ? `DataFrame[${snakeToTitleCase(feature.kind.foreignNamespace)}]`
        : snakeToTitleCase(feature.kind.foreignNamespace);
    case "hasOne":
      return snakeToTitleCase(feature.kind.foreignNamespace);
    case "featureTime":
      return "datetime";
    case undefined:
      return "unknown";
    default:
      compileOnlyAssertNever(feature.kind);
      return "unknown";
  }
};

/**
 * Scalars and windowed aggregations are the value-bearing kinds: they're the
 * only ones that carry typeInfo, version metadata, no_display, and
 * materialization info. Callers narrowing on that union should use this.
 */
export const featureIsVersionable = (
  kind: FeatureInterfaceKind,
): kind is FeatureInterfaceScalarKind | FeatureInterfaceWindowedFeatureKind =>
  kind.type === "scalar" || kind.type === "windowed";

export function featureIsVersioned(
  feature: FeatureInterface,
): feature is VersionedFeatureInterface {
  return featureIsVersionable(feature.kind) && feature.kind.version != null;
}

export type VersionedFeatureInterface = FeatureInterface & {
  kind: (FeatureInterfaceScalarKind | FeatureInterfaceWindowedFeatureKind) & {
    version: VersionInfoInterface;
  };
};

export function composeFeatureFilters(
  features: FeatureInterface[],
  opts?: FeaturesOptions,
): FeatureInterface[] {
  const versionedMaybe = opts?.defaultVersionOnly
    ? filterOutNonDefaultFeatureVersions(features)
    : features;
  const windowedMaybe = opts?.filterWindowedChildren
    ? filterOutWindowedChildren(versionedMaybe)
    : versionedMaybe;

  return windowedMaybe;
}

const filterOutWindowedChildren = memoize((features: FeatureInterface[]) => {
  return features.filter((f) => f.kind.type !== "scalar" || !f.kind.isWindowedChild);
});

/**
 * Filter out versioned features that are not the default version
 */
const filterOutNonDefaultFeatureVersions = memoize((features: FeatureInterface[]) => {
  return features.filter(
    (f) =>
      !featureIsVersionable(f.kind) ||
      f.kind.version == null ||
      f.kind.version.default === f.kind.version.version,
  );
});

export const chalkInternalFields = new Set(["__chalk_observed_at__", "__ts__"]);

export const isChalkInternalFqn = (featureFqn: string): boolean => {
  const field = featureFqn.split(".").at(-1);
  return !!field && chalkInternalFields.has(field);
};

export const isHasOne = (kind: FeatureInterfaceKind): kind is FeatureInterfaceHasOneKind =>
  kind.type === "hasOne";

export const isHasMany = (kind: FeatureInterfaceKind): kind is FeatureInterfaceHasManyKind =>
  kind.type === "hasMany";

export const isRelationKind = (
  kind: FeatureInterfaceKind,
): kind is FeatureInterfaceHasOneKind | FeatureInterfaceHasManyKind =>
  isHasMany(kind) || isHasOne(kind);

export const isFeatureTime = (feature: FeatureInterface): boolean =>
  feature.kind.type === "featureTime";

export const isWindowedParentOrChild = (feature: FeatureInterface): boolean =>
  isWindowedChildFeature(feature) || featureIsWindowedParent(feature);

export const isWindowedParentFeature = (feature: FeatureInterface): boolean =>
  feature.kind.type === "windowed";

export const isWindowedChildFeature = (feature: FeatureInterface): boolean =>
  feature.kind.type === "scalar" && Boolean(feature.kind.isWindowedChild);

export const isWindowedChildFeatureName = (featureName: string): boolean =>
  isWindowedChildName(featureName, true);

export const featureIsPrimary = (feature: FeatureInterface): boolean =>
  feature.kind.type === "scalar" && feature.kind.isPrimary;

export const windowedBaseFQN = (fqn: string): string => {
  return fqn.replace(windowPeriodAtEndRegex, "");
};

export const transformWindowedFeatureName = (featureName: string): string => {
  return transformWindowedName(featureName, true);
};

export const getFeatureDisplayName = (feature: FeatureInterface) =>
  getFeatureFqnDisplayName(feature.fqn);

export const getFeatureFqnDisplayName = (fqn: string) =>
  transformWindowedFeatureName(fqnToName(fqn));

export function typeInfoIsLegacy(
  typeInfo: LegacyFeatureTypeInfo | ArrowTypeWrapper,
): typeInfo is LegacyFeatureTypeInfo {
  return "scalarKind" in typeInfo;
}
export const TIME_FEATURE_KIND_SET = new Set([
  "datetime",
  "date",
  "timestamp",
  "date64",
  "time64",
  "date32",
  "time32",
]);

export const featureKindIsTimelike = (kind: FeatureInterfaceKind) => {
  if (kind.type !== "scalar" || kind.typeInfo == null) return false;
  if (typeInfoIsLegacy(kind.typeInfo)) {
    return TIME_FEATURE_KIND_SET.has(kind.typeInfo.scalarKind);
  } else {
    return (
      kind.typeInfo.arrowType.arrowTypeEnum.case != null &&
      TIME_FEATURE_KIND_SET.has(kind.typeInfo.arrowType.arrowTypeEnum.case)
    );
  }
};

export const getFeatureKindMaterializationInfo = (
  kind: FeatureInterfaceKind,
): WindowAggregation | undefined => {
  if (featureIsVersionable(kind)) {
    return kind.windowInfo?.aggregation;
  }

  return undefined;
};

export const featureKindIsMaterialized = (
  kind: FeatureInterfaceKind,
): kind is FeatureInterfaceScalarKind | FeatureInterfaceWindowedFeatureKind => {
  return getFeatureKindMaterializationInfo(kind) != null;
};

export const NUMERIC_FEATURE_KIND_SET = new Set([
  "int",
  "float",
  "float64",
  "int64",
  "int8",
  "int16",
  "int32",
  "uint8",
  "uint16",
  "uint32",
  "uint64",
  "float16",
  "float32",
]);

export const LIST_FEATURE_KIND_SET = new Set(["list", "fixedSizeList", "largeList"]);

export const STRING_FEATURE_KIND_SET = new Set(["string", "utf8", "largeUtf8"]);

export const isNumericFeatureKind = (kind: FeatureInterfaceKind) => {
  if (kind.type !== "scalar" || kind.typeInfo == null) return false;
  if (typeInfoIsLegacy(kind.typeInfo)) {
    return NUMERIC_FEATURE_KIND_SET.has(kind.typeInfo.scalarKind);
  } else {
    return (
      kind.typeInfo.arrowType.arrowTypeEnum.case != null &&
      NUMERIC_FEATURE_KIND_SET.has(kind.typeInfo.arrowType.arrowTypeEnum.case)
    );
  }
};

export const isListFeatureKind = (kind: FeatureInterfaceKind) => {
  if (kind.type !== "scalar" || kind.typeInfo == null) return false;
  if (typeInfoIsLegacy(kind.typeInfo)) {
    return kind.typeInfo.scalarKind.includes("list");
  } else {
    return (
      kind.typeInfo.arrowType.arrowTypeEnum.case != null &&
      LIST_FEATURE_KIND_SET.has(kind.typeInfo.arrowType.arrowTypeEnum.case)
    );
  }
};

export const isStringFeatureKind = (kind: FeatureInterfaceKind) => {
  if (kind.type !== "scalar" || kind.typeInfo == null) return false;
  if (typeInfoIsLegacy(kind.typeInfo)) {
    return kind.typeInfo.scalarKind === "str";
  } else {
    return (
      kind.typeInfo.arrowType.arrowTypeEnum.case != null &&
      STRING_FEATURE_KIND_SET.has(kind.typeInfo.arrowType.arrowTypeEnum.case)
    );
  }
};

export interface FeaturePath {
  path: string;
  underlying: string;
  type: string;
}

const MAX_DEPTH = 3;

export interface FetchFeaturePathsArgs {
  namespace: string | null;
  maxDepth: number;
  includeHasMany: boolean;
  includeWindowedChildren: boolean;
  includeRelationFeatures: boolean;
}

const DEFAULT_OPTIONS = {
  maxDepth: MAX_DEPTH,
  namespace: null,
  includeHasMany: false,
  includeWindowedChildren: false,
  includeRelationFeatures: false,
} satisfies FetchFeaturePathsArgs;

/**
 * Given a feature namespace, fetch all the possible feature paths that could
 * be queried for in that namespace, excluding circular paths and only going as far as `maxDepth`.
 *
 * By default, ignore "has-many" and "feature-time" feature paths entirely, and do not include non-scalar
 * (i.e. has-one and has-many) features as well.
 */
export const recursivelyFetchFeaturePaths = (
  graph: GraphInterface,
  opts: Partial<FetchFeaturePathsArgs> | null = null,
): FeaturePath[] =>
  recursivelyFetchHasOnePathsHelper(graph, { ...DEFAULT_OPTIONS, ...opts }, null, 0, []);

const recursivelyFetchHasOnePathsHelper = (
  graph: GraphInterface,
  opts: FetchFeaturePathsArgs,
  featureName: string | null,
  depth: number,
  namespaces: string[],
): FeaturePath[] => {
  const { namespace, maxDepth, includeWindowedChildren, includeHasMany, includeRelationFeatures } =
    opts;
  const featuresForNamespace =
    namespace != null
      ? graph.getFeaturesForNamespace(namespace, {
          filterWindowedChildren: !includeWindowedChildren,
          defaultVersionOnly: true,
        })
      : graph.getAllFeatures({
          filterWindowedChildren: !includeWindowedChildren,
          defaultVersionOnly: true,
        });

  if (depth === maxDepth) {
    // if we have hit our max depth, stop recursing
    return [];
  }

  // slow but we're not going that deep
  if (namespace != null && namespaces.includes(namespace)) {
    // don't recurse into a namespace that we've already explored.
    // Arguably this is wrong, but I want to avoid circular has-one references,
    // e.g. user.transaction -> transaction.user -> user.transaction -> .....
    return [];
  }

  return fastCompact(
    featuresForNamespace.flatMap((f) => {
      const kind = f.kind;
      if (isRelationKind(kind)) {
        if (kind.type === "hasMany" && !includeHasMany) {
          return null;
        }
        if (kind.join != null && kind.join.exprType.case === "binaryExpr") {
          const nextNamespace = kind.foreignNamespace;
          const nextFeatureName = f.name;
          /**
           * Imagine some features:
           * cat.favorite_treat = has_one(treat)
           * treat.primary_flavor = has_one(flavor)
           * flavor.main_ingredient = has_one(ingredient)
           * ingredient.health_value: int
           *
           * We want to turn the joins here into `cat.favorite_treat.primary_flavor.main_ingredient.health_value`
           *
           */
          const subFeatures = recursivelyFetchHasOnePathsHelper(
            graph,
            { ...opts, namespace: nextNamespace },
            nextFeatureName,
            depth + 1,
            namespace != null ? namespaces.concat(namespace) : namespaces,
          ).map((fp) => ({
            ...fp,
            path: featureName != null ? `${featureName}.${fp.path}` : `${f.namespace}.${fp.path}`,
          }));

          return includeRelationFeatures
            ? [
                {
                  path: featureName != null ? `${featureName}.${fqnToName(f.fqn)}` : f.fqn,
                  underlying: f.fqn,
                  type: nameForFeatureType(f, true),
                },
                ...subFeatures,
              ]
            : subFeatures;
        } else {
          return null;
        }
      } else if (kind.type === "scalar") {
        // "base case" is a scalar feature (unless we stopped recursing!)
        return [
          {
            path: featureName != null ? `${featureName}.${fqnToName(f.fqn)}` : f.fqn,
            underlying: f.fqn,
            type: nameForFeatureType(f, true),
          },
        ];
      } else {
        // ignore has-many (if not specified) and feature time features
        return null;
      }
    }),
  );
};

export interface RouteForFeatureArgs {
  projectId: string;
  environmentId: string;
  featureFQN: string;
  activeDeploymentId: string | undefined;
  branchName: string | undefined;
  deploymentId: string | undefined;
}

export function routeForFeature({
  projectId,
  environmentId,
  featureFQN,
  activeDeploymentId,
  branchName,
  deploymentId,
}: RouteForFeatureArgs): string {
  if (deploymentId == null || deploymentId === activeDeploymentId) {
    return Routes.project.environment.feature(projectId, environmentId, featureFQN);
  }

  if (branchName != null) {
    return Routes.project.environment.branchDeploymentFeature(
      projectId,
      environmentId,
      branchName,
      deploymentId,
      featureFQN,
    );
  }
  return Routes.project.environment.deploymentFeature(
    projectId,
    environmentId,
    deploymentId,
    featureFQN,
  );
}
